import React from 'react'
import { Auth } from 'aws-amplify'
import { Image, Col, Row, Dropdown } from 'react-bootstrap'
import { ChevronDown } from 'react-feather'

import * as AppDefinitions from '../../App.d'
import * as I from '../../utilities/me'
import * as ApiDefinitions from '../../../../back-end/utilities/apiDefinitions'

import { Dialog } from '../ui/dialog/dialog'
import { SubscriptionItem } from './subscriptionItem'
import { refreshSession } from '../../utilities/login'

interface UserButtonProps {
	userAttributes: AppDefinitions.CognitoUserAttributes
	permission: AppDefinitions.Permission
	accounts: ApiDefinitions.Account[]
	context: AppDefinitions.ProductContext
	selectedOrg?: ApiDefinitions.AccountXeroOrg
}

const findOutAccountCanUseKeypay = (props: UserButtonProps) => {
	if (props.accounts) {
		return props.accounts.some((acc) => acc.canUseKeypay)
	}
	return false
}

export const UserButton = (props: UserButtonProps) => {
	const [showDialog, updateShowDialog] = React.useState<boolean>(false)
	const [currentAccountHasKeypay, updateCurrentAccountHasKeypay] = React.useState<boolean>(findOutAccountCanUseKeypay(props))

	React.useEffect(() => {
		updateCurrentAccountHasKeypay(findOutAccountCanUseKeypay(props))
	}, [props.accounts, props.selectedOrg]) // eslint-disable-line

	const signOut = () => {
		Auth.signOut()
			.then(() => {
				window.location.href = '/'
			})
			.catch((err: Error) => console.log(err))
	}

	const onAccountSwitch = async (eventKey: string) => {
		updateShowDialog(false)

		const accountID = eventKey
		const selectedAccount = props.accounts.find((account) => account.accountID === accountID)
		await Auth.currentAuthenticatedUser()

		if (selectedAccount) {
			const user = await Auth.currentAuthenticatedUser()

			await Auth.updateUserAttributes(user, {
				'custom:currentAccountId': accountID,
				'custom:userAccountId': selectedAccount.userAccountID,
				'custom:role': JSON.stringify({
					os: selectedAccount.osPermissionID,
					lc: selectedAccount.lcPermissionID,
					tayg: selectedAccount.taygPermissionID,
					subsync: selectedAccount.subsyncPermissionID,
				}),
			})
			await refreshSession()
			window.location.href = '/'
			window.location.reload()
		}
	}

	return (
		<Dropdown>
			<Dropdown.Toggle className="no-caret" variant="secondary" id="userprofile">
				<Row>
					<Col xs="auto">
						<Image
							src={`https://ui-avatars.com/api/?name=${props.userAttributes.given_name}+${props.userAttributes.family_name}`}
							roundedCircle
							className="avatar"
						/>
					</Col>
					<Col xs="auto" className="dropdown-content">
						<Row>
							<Col>
								{props.userAttributes.given_name} {props.userAttributes.family_name}
							</Col>
						</Row>
						<Row>
							<Col>
								<small>{props.permission ? I.am(props.permission[props.context]) : ''}</small>
							</Col>
						</Row>
					</Col>
					<Col xs="auto" className="dropdown-end">
						<ChevronDown />
					</Col>
				</Row>
			</Dropdown.Toggle>
			<Dropdown.Menu>
				<Dropdown.Item href={'/'} disabled={!props.permission[props.context] || props.accounts.length === 0}>
					Home
				</Dropdown.Item>
				<Dropdown.Item href={`/users`} disabled={!props.permission[props.context] || props.accounts.length === 0}>
					User Settings
				</Dropdown.Item>
				{props.context !== 'oversight' ? (
					<Dropdown.Item href={`/branding`} disabled={!props.permission[props.context] || props.accounts.length === 0}>
						Custom Branding
					</Dropdown.Item>
				) : null}
				{props.context === 'oversight' ? (
					<>
						<Dropdown.Item href={`/tags`} disabled={!props.permission[props.context] || props.accounts.length === 0}>
							Tag Settings
						</Dropdown.Item>
						<Dropdown.Item href={`/threshold`} disabled={!props.permission[props.context] || props.accounts.length === 0}>
							Threshold Settings
						</Dropdown.Item>
					</>
				) : null}
				{props.context === 'leavecal' ? (
					<>
						{I.have('admin', props.permission[props.context]) ? (
							<>
								<SubscriptionItem
									selectedXeroOrg={props.selectedOrg?.xeroOrg}
									productCode="95a516a0-5aaf-4445-ba80-d20f3aaf855a"
									disable={!props.permission || props.accounts.length === 0}
								/>
								<Dropdown.Item href={`/google/integration`} disabled={!props.permission[props.context] || props.accounts.length === 0}>
									Google Integration
								</Dropdown.Item>
								<Dropdown.Item href={`/combinedcal`} disabled={!props.permission[props.context] || props.accounts.length === 0}>
									Combined Calendar
								</Dropdown.Item>
							</>
						) : null}
					</>
				) : null}
				{props.context === 'tayg' ? (
					<>
						{I.have('admin', props.permission[props.context]) ? (
							<>
								<SubscriptionItem
									selectedXeroOrg={props.selectedOrg?.xeroOrg}
									productCode="0b229c2a-4641-42a8-a66a-2dd0a37a0a33"
									disable={!props.permission || props.accounts.length === 0}
								/>
							</>
						) : null}
					</>
				) : null}
				{props.context === 'subsync' ? (
					<>
						{I.have('admin', props.permission[props.context]) ? (
							<>
								<SubscriptionItem
									selectedXeroOrg={props.selectedOrg?.xeroOrg}
									productCode="45607e76-1605-4f2a-85cf-5012327bf0ae"
									disable={!props.permission || props.accounts.length === 0}
								/>
							</>
						) : null}
					</>
				) : null}
				{I.have('admin', props.permission[props.context]) ? (
					<>
						<Dropdown.Item href={`/xero/connections`}>Xero Connections</Dropdown.Item>
						{/* <Dropdown.Item href={`/google/integration`}>Google Integration</Dropdown.Item> */}
					</>
				) : null}
				{I.have('admin', props.permission[props.context]) && props.context === 'subsync' ? (
					<>
						<Dropdown.Item href={`/stripe/connections`}>Stripe Connections</Dropdown.Item>
						{/* <Dropdown.Item href={`/google/integration`}>Google Integration</Dropdown.Item> */}
					</>
				) : null}
				{I.have('admin', props.permission[props.context]) && currentAccountHasKeypay ? (
					<>
						<Dropdown.Item href={`/keypay/connections`}>Keypay Connections</Dropdown.Item>
					</>
				) : null}
				{props.accounts && props.accounts.length > 1 ? (
					<>
						<Dropdown.Item onClick={() => updateShowDialog(true)}>Switch Account</Dropdown.Item>
					</>
				) : null}
				<Dropdown.Divider />
				<Dropdown.Item
					onClick={() => {
						window.open(`https://help.finlert.com/${props.context.replace('tayg', 'tax-as-you-go')}/`, '_blank', 'noopener,noreferrer')
					}}
				>
					Help Guides
				</Dropdown.Item>
				<Dropdown.Divider />
				<Dropdown.Item onClick={signOut}>Log out</Dropdown.Item>
				<Dialog
					show={showDialog}
					handleClose={() => updateShowDialog(false)}
					titleText={'Which account do you want to log into?'}
					elements={props.accounts}
					currentElem={props.selectedOrg ? props.selectedOrg!.account!.accountID : props.userAttributes['custom:currentAccountId']}
					handleLogIn={(eventKey) => onAccountSwitch(eventKey)}
				/>
			</Dropdown.Menu>
		</Dropdown>
	)
}
